/**
 * LINE 動作（按鈕連結）欄位的組合與解析
 *
 * 編輯器用語意欄位保存（actionType: 'tel' 存 phone、'share' 存 shareText），
 * 但 LINE 只認 uri，因此一律同步組出 uri；
 * 圖片輪播這類只存得下 LINE 原生格式的場景，載入時再從 uri 反推回語意欄位。
 */

/** 分享給好友的 LINE URL scheme（只有手機版 LINE 支援） */
export const LINE_SHARE_URI_PREFIX = 'https://line.me/R/share?text=';

/** 官方帳號加入頁。basicId 可帶或不帶 @ */
export function officialAccountAddUrl(basicId) {
  const id = String(basicId ?? '').trim().replace(/^@+/, '');
  if (!id || !/^[a-zA-Z0-9._-]+$/.test(id)) return '';
  return `https://line.me/R/ti/p/@${id}`;
}

/** 圖文選單「邀請加入官方帳號」要分享出去的文字 */
export function buildOfficialAccountInviteText(basicId, accountName) {
  const url = officialAccountAddUrl(basicId);
  if (!url) return '';
  const name = String(accountName ?? '').trim();
  const lead = name ? `邀請你加入「${name}」官方帳號` : '邀請你加入官方帳號';
  return `${lead}\n${url}`;
}

export function buildOfficialAccountInviteUri(basicId, accountName) {
  const text = buildOfficialAccountInviteText(basicId, accountName);
  if (!text) return '';
  return `${LINE_SHARE_URI_PREFIX}${encodeURIComponent(text)}`;
}

/** 撥號用號碼：LINE 的 tel: 只吃數字與 + # *，「-」與空白要去掉 */
export function normalizeDialNumber(phone) {
  return String(phone ?? '').replace(/[^0-9+#*]/g, '');
}

/**
 * 依 actionType 組出實際要送給 LINE 的 uri
 * @param {Object} value - { actionType, uri, phone, shareText }
 * @returns {string} 組不出來時回空字串
 */
export function buildActionUri(value) {
  const v = value || {};
  if (v.actionType === 'tel') {
    const dial = normalizeDialNumber(v.phone);
    return dial ? `tel:${dial}` : '';
  }
  if (v.actionType === 'share') {
    const text = String(v.shareText ?? '').trim();
    return text ? `${LINE_SHARE_URI_PREFIX}${encodeURIComponent(text)}` : '';
  }
  return v.uri || '';
}

/**
 * 由已儲存的 uri 反推編輯器欄位
 * @param {string} uri
 * @returns {{ actionType: string, uri: string, phone?: string, shareText?: string }}
 */
export function actionValueFromUri(uri) {
  const u = String(uri ?? '');
  if (u.startsWith('tel:')) {
    return { actionType: 'tel', uri: u, phone: u.slice(4) };
  }
  if (u.startsWith(LINE_SHARE_URI_PREFIX)) {
    const raw = u.slice(LINE_SHARE_URI_PREFIX.length);
    let shareText = raw;
    try {
      shareText = decodeURIComponent(raw);
    } catch {
      // 編碼壞掉就顯示原字串，讓使用者自己修
    }
    return { actionType: 'share', uri: u, shareText };
  }
  return { actionType: 'uri', uri: u };
}
